import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import { InstallProvider } from "./lib/install.jsx";
import { IOSInstallModal } from "./components/InstallButton.jsx";
import { stopSpeaking } from "./lib/speech.js";
import { bumpSessionCount } from "./lib/appFeedback";
import Landing from "./pages/Landing.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import AuthCallback from "./pages/AuthCallback.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import SubjectChapters from "./pages/SubjectChapters.jsx";
import ChapterDetail from "./pages/ChapterDetail.jsx";
import MockTest from "./pages/MockTest.jsx";
import MapWork from "./pages/MapWork.jsx";
import Profile from "./pages/Profile.jsx";
import Paywall from "./pages/Paywall.jsx";
import Privacy from "./pages/Privacy.jsx";
import Terms from "./pages/Terms.jsx";
import DeleteAccount from "./pages/DeleteAccount.jsx";
import AppLayout from "./components/AppLayout.jsx";
import "./App.css";

function ProtectedShell({ requireAccess = true }) {
  const { user, access, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-brimi-navy">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAccess && access && !access.is_active) return <Navigate to="/paywall" replace />;
  return <AppLayout />;
}

// Stop any active text-to-speech whenever the route changes
// (e.g. student moves to another chapter, opens dashboard, etc.)
function StopSpeechOnRouteChange() {
  const location = useLocation();
  useEffect(() => {
    stopSpeaking();
  }, [location.pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    // Bump the session counter once per app boot for the rating nudge.
    bumpSessionCount();
  }, []);

  return (
    <AuthProvider>
      <InstallProvider>
        <BrowserRouter>
          <Toaster richColors position="top-center" />
          <IOSInstallModal />
          <StopSpeechOnRouteChange />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route element={<ProtectedShell requireAccess={false} />}>
              <Route path="/paywall" element={<Paywall />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
            <Route element={<ProtectedShell />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/subject/:subject/:cls" element={<SubjectChapters />} />
              <Route path="/chapter/:chapterId" element={<ChapterDetail />} />
              <Route path="/mock-test" element={<MockTest />} />
              <Route path="/map-work" element={<MapWork />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </InstallProvider>
    </AuthProvider>
  );
}
